import {
  Stack,
  Button,
  Dialog,
  Container,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useBoolean } from 'src/hooks/use-boolean';
import UserSearchAutocomplete, { UserOption } from 'src/components/ui-kit/user-search-autocomplete';
import StockTableView from 'src/sections/stock/stock-report/stock-table-view';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { enqueueSnackbar } from 'notistack';

export default function StockReportView() {
  const printDialog = useBoolean(false);
  const isPrinting = useBoolean(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  const handleOpenPrint = () => {
    printDialog.onTrue();
  };

  const handleClosePrint = () => {
    setSelectedUser(null);
    printDialog.onFalse();
  };

  const handleSubmitPrint = async () => {
    isPrinting.onTrue();
    try {
      const res = await axios.get(endpoints.printReport.stockUsers, {
        params: {
          date: selectedDate?.format('YYYY-MM-DD'),
          userId: selectedUser?.value,
        },
        responseType: 'blob',
      });

      const contentType = res.headers['content-type'] || '';

      let blob: Blob;
      if (contentType.includes('application/pdf')) {
        // Backend already streams PDF
        blob = res.data as Blob;
      } else if (contentType.includes('application/json')) {
        // Backend returns JSON wrapper { buffer, contentType, filename }
        const text = await (res.data as Blob).text();
        const json = JSON.parse(text);
        // Support Node Buffer object or base64 string
        if (json?.buffer?.type === 'Buffer' && Array.isArray(json.buffer.data)) {
          const bytes = new Uint8Array(json.buffer.data);
          blob = new Blob([bytes], { type: json.contentType || 'application/pdf' });
        } else if (typeof json?.buffer === 'string') {
          const base64 = json.buffer;
          const raw = atob(base64);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
          blob = new Blob([bytes], { type: json.contentType || 'application/pdf' });
        } else {
          throw new Error('Invalid JSON payload for PDF');
        }
      } else {
        // Fallback: force as PDF
        blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      }
      const objUrl = URL.createObjectURL(blob);

      // Open in a new tab using anchor element to avoid popup blocker
      const link = document.createElement('a');
      link.href = objUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar('เตรียมเอกสารสำหรับพิมพ์แล้ว', { variant: 'success' });
    } catch (error) {
      toastError(error);
    } finally {
      setSelectedUser(null);
      printDialog.onFalse();
      isPrinting.onFalse();
    }
  };
  return (
    <Container>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            รายงานสต็อก
          </Typography>
          <Button variant="contained" onClick={handleOpenPrint}>
            พิมพ์สต๊อก
          </Button>
        </Stack>
        <StockTableView />
      </Stack>

      <Dialog open={printDialog.value} onClose={handleClosePrint} maxWidth="xs" fullWidth>
        <DialogTitle>พิมพ์สต๊อก</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DatePicker
              label="วันที่"
              value={selectedDate}
              onChange={(v) => setSelectedDate(v)}
              sx={{ width: 1 }}
              format="DD MMMM YYYY"
            />
            <UserSearchAutocomplete value={selectedUser} onChange={setSelectedUser} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClosePrint}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" onClick={handleSubmitPrint} loading={isPrinting.value}>
            ยืนยัน
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
